import React, { useRef, PropsWithChildren, ReactElement, type JSX } from 'react';
import { Node } from './Node';
import { Placeholder } from './Placeholder';
import { HierarchyFilter } from './types';
import { useTreeContext, useDropRoot, useContainerClassName } from './hooks';
import { getFilterExpression } from './utils';
import { useTranslation } from 'react-i18next';

type Props = PropsWithChildren<{
    parentNode: any;
    depth: number;
    nodes: any;
    disabled?: boolean;
    controlledSelected?: boolean;
}>;

export const Container = <T,>(props: Props): ReactElement => {
    const treeContext = useTreeContext<T>();
    const ref = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const [isOver] = useDropRoot<T>(ref, props?.parentNode, props?.disabled ?? false);

    const className = useContainerClassName(isOver);

    const [nodes, setNodes] = React.useState(props?.nodes);

    React.useEffect(() => {
        if (treeContext?.pagination) {
            setNodes(props?.nodes?.[treeContext?.pagination?.records ?? 'records']);
        } else {
            setNodes(props?.nodes);
        }
    }, [props?.nodes]);

    React.useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [treeContext.hierarchyMappingData]);

    const getFilterPayload = (node: any): HierarchyFilter[] => {
        if (typeof treeContext?.filters === 'function') {
            return treeContext?.filters?.(node) ?? [];
        }

        if (treeContext?.filters?.length) {
            return treeContext?.filters.filter((filter) => !getFilterExpression(node, filter));
        }

        return [];
    };

    const filteredHierarchyNodes = nodes?.map?.((node: any, index: number) => {
        const filterPayload = getFilterPayload(node);
        const isHiddenNode = Boolean(filterPayload?.find((filter) => filter.action === 'hidden'));
        if (isHiddenNode) return null;

        return (
            <React.Fragment key={node.id}>
                {!props?.disabled && (
                    <Placeholder
                        depth={props?.depth}
                        listCount={props?.nodes?.length}
                        dropTargetId={props?.parentNode?.id}
                        index={index}
                    />
                )}
                <Node
                    {...node}
                    parentNode={props?.parentNode}
                    depth={props.depth}
                    controlledSelected={props?.controlledSelected}
                    disabled={props?.disabled}
                    filterPayload={filterPayload}
                />
            </React.Fragment>
        );
    });

    const getPaginationNode = (): JSX.Element => {
        const recordsKey = treeContext?.pagination?.records ?? 'records';
        const totalKey = treeContext?.pagination?.total ?? 'total';
        const total = props?.nodes?.[totalKey] ?? 0;
        // Base this on the ACTUALLY loaded record count, not paginationMappingData — the
        // latter is separate local state that resets on remount (e.g. hamburger close/open)
        // even when hierarchyMappingData already holds every page from an earlier session,
        // which made this button reappear and re-request already-loaded pages (duplicates).
        const loadedCount = props?.nodes?.[recordsKey]?.length ?? 0;
        const moreButtonId = `more-button-${props?.parentNode?.id}`;
        const isLoadMoreLoading = treeContext?.loadingNode?.includes(moreButtonId);
        const canShowPaginationNode = Boolean(treeContext?.pagination && (loadedCount < total || isLoadMoreLoading));

        if (!canShowPaginationNode) return <></>;

        return (
            <Node
                id={moreButtonId}
                displayName={isLoadMoreLoading ? t('COMMON:LOADING') : t('COMMON:SHOW_MORE')}
                parentNode={props?.parentNode}
                depth={props.depth}
            />
        );
    };

    return (
        <div ref={ref} className="dnd-container" style={{ ...className, listStyleType: 'none', height: '100%' }}>
            {filteredHierarchyNodes?.every?.((item: any) => item === null) && props?.depth === 0
                ? treeContext?.noDataFoundComponent
                : filteredHierarchyNodes}
            {getPaginationNode()}
            {!props?.disabled && (
                <Placeholder depth={props.depth} listCount={props?.nodes?.length} dropTargetId={props.parentNode?.id} />
            )}
        </div>
    );
};
